import * as _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { ICenter, ICenterToCenterDistance, IPosition } from '@nkr/models/index';
import { PlaceholderTable, TableWrapper, TooltipGlobalStyle } from './styles';
import { Glyphicon, OverlayTrigger, Tooltip, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';
import PopoverTemplate from '@nkr/components/popover-template';
import { AnatomyMaxPreference } from '@nkr/enums/anatomy-max-preference';

enum Group {
    DonorCenter = 2,
    TargetWithin400 = 3,
    TargetBeyond400 = 4
}

interface IGroupSort { groupId: Group, property: string, direction: string }

const defaultSort = {
    properties: ['isAdpPartner', 'fvTarget'],
    directions: ['asc', 'desc']
};

const initialGroupSort: IGroupSort[] = [
    { groupId: Group.TargetWithin400, property: '', direction: '' },
    { groupId: Group.TargetBeyond400, property: '', direction: '' }
];


interface IToggleTooltipProps {
    id: string;
    title: string;
    items: string[];
}
interface ICenterForList {
    centerId: number;
    alias: string;
    fvTarget: number;
    isAdpPartner: boolean;
    ncsDeficit: number;
    isDonorCenter: boolean;
    isWithin400: boolean;
    distance: number;
    hasAbPatients: boolean;
    anatomyPrefs: boolean;
    declineRatio: number;
    canUtilizeA2toBPatients: boolean;
    ncsGreen: boolean;
}

interface IProps {
    position: IPosition;
    centerList: ICenter[];
    distanceBetweenCentersList: ICenterToCenterDistance[];
    onCreatePlaceholder: (centerId: number) => void;
    onCancel: () => void;
}

const getSortedItems = (list: ICenterForList[], groupSort: IGroupSort[], group: Group) => {
    let gs = groupSort.find(x => x.groupId == group);
    if (!gs || gs.property == '') return _.orderBy(list, defaultSort.properties, defaultSort.directions as any);
    return _.orderBy(list, [gs.property], [gs.direction as any]);
};

const PlaceHolderTable = (props: IProps) => {
    const [groupSort, setGroupSort] = useState<IGroupSort[]>(initialGroupSort);
    const [suppress, setSuppress] = useState<boolean>(true);
    const handleUpdateSuppress = (value: boolean) => setSuppress(value);

    const handleClosePlaceholder = (ev: MouseEvent) => {
        const popup = document.getElementById('ph-select-' + props.position.donor.id);
        if (popup && popup.contains(ev.target as Node)) return; // click inside popup — leave it open

        ev.preventDefault();
        ev.stopPropagation();
        props.onCancel();
    };

    const ToggleTooltip = ({ id, title, items, ...rest }: IToggleTooltipProps & any) => (
        <Tooltip {...rest} id={id} className={`ph-toggle-tooltip ${rest.className || ''}`}>
            <div style={{ textAlign: 'left' }}>
                <strong>{title}</strong>
                <ul style={{ paddingLeft: 18, margin: '5px 0 0 0' }}>
                    {items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
            </div>
        </Tooltip>
    );

    useEffect(() => {
        document.body.addEventListener('click', handleClosePlaceholder);
        return () => document.body.removeEventListener('click', handleClosePlaceholder);
    }, []);

    const handlePlaceholderSort = (groupId: Group, property: string, direction: string) => {
        const gs = _.cloneDeep(groupSort);
        gs.forEach(item => {
            if (item.groupId == groupId) {
                if (item.property == property) {
                    item.direction = item.direction == 'asc' ? 'desc' : 'asc';
                } else {
                    item.property = property;
                    item.direction = direction;
                }
            }
        });
        setGroupSort(gs);
    };

    const centerList: ICenterForList[] = props.centerList.map(center => {
        const { id: centerId, alias, fvTarget, ncsDeficit, isAdpPartner, declineRatio, hasAbPatients, ncsRank } = center;
        const donorCenterId = props.position.donor.rdnCenterId > 0 ? props.position.donor.rdnCenterId : props.position.donor.center.id;
        const isDonorCenter = center.id == donorCenterId;
        const centerToCenter = props.distanceBetweenCentersList.find(x => x.donorCenterId == donorCenterId && x.recipientCenterId == center.id);
        const isWithin400 = centerToCenter ? centerToCenter.distanceBetweenCenters <= 400 : false;
        const distance = centerToCenter ? centerToCenter.distanceBetweenCenters : 0;
        const anatomyPrefs = Utility.donorMeetsAnatomyPref(props.position.donor, center);
        const canUtilizeA2toBPatients = center.canUtilizeA2toBPatients;
        const ncsGreen = ncsRank < -1000;
        const displayFvTarget = isAdpPartner ? fvTarget : ncsDeficit;

        return { centerId, alias, fvTarget: displayFvTarget, ncsDeficit, isAdpPartner, isDonorCenter, isWithin400, distance, hasAbPatients, anatomyPrefs, declineRatio, canUtilizeA2toBPatients, ncsGreen };

    });

    const donorCenterGroup = [
        {
            id: Group.DonorCenter,
            label: 'Donor Center',
            options: centerList.filter(x => x.isDonorCenter)
        }
    ];

    const applySuppress = (list: ICenterForList[]) => {
        if (!suppress) return list;

        const isA2OrA2b = props.position.donor.bloodType == 'A2' || props.position.donor.bloodType == 'A2B';
        const isAbOrA2bBloodTypes = ['A1B', 'AB', 'A2B'];
        const isAbOrA2b = isAbOrA2bBloodTypes.includes(props.position.donor.bloodType);

        return list.filter(x =>
            ((!x.isAdpPartner ? x.ncsDeficit >= 2 : false) ||
                (x.isAdpPartner ? x.fvTarget >= 2 : false)) &&
            x.declineRatio < 30 &&
            x.anatomyPrefs &&
            (isAbOrA2b ? x.hasAbPatients == true : true) &&
            (isA2OrA2b ? x.canUtilizeA2toBPatients : true)
        );
    };

    const baseTargetFilter = (x: ICenterForList) =>
        !x.isDonorCenter &&
        !x.ncsGreen &&
        ((x.isAdpPartner && x.fvTarget > 0) || (!x.isAdpPartner && x.ncsDeficit > 0)) &&
        x.fvTarget >= 1;

    const targetWithin400 = [
        {
            id: Group.TargetWithin400,
            label: 'Target Centers',
            options: getSortedItems(
                applySuppress(centerList.filter(x => x.isWithin400 && baseTargetFilter(x))),
                groupSort,
                Group.TargetWithin400
            )
        }
    ];

    const targetBeyond400 = [
        {
            id: Group.TargetBeyond400,
            label: 'Target Centers',
            options: getSortedItems(
                applySuppress(centerList.filter(x => !x.isWithin400 && baseTargetFilter(x))),
                groupSort,
                Group.TargetBeyond400
            )
        }
    ];

    const renderSortIndicator = (groupId: Group, property: string) => {
        const gs = groupSort.find(x => x.groupId == groupId);
        if (gs && gs.property == property) {
            const style = { fontSize: 14, pointerEvents: 'none' as const };
            return gs.direction == 'asc'
                ? <Glyphicon glyph='triangle-top' style={style} />
                : <Glyphicon glyph='triangle-bottom' style={style} />;
        }
        return null;
    };

    const abBloodTypes = ['A1B', 'AB', 'A?B'];
    const isAbAbo = abBloodTypes.includes(props.position.donor.bloodType);
    const isA2OrA2b = props.position.donor.bloodType == 'A2' || props.position.donor.bloodType == 'A2B';
    const isA2b = props.position.donor.bloodType == 'A2B';
    const isA2 = props.position.donor.bloodType == 'A2';
    const isDonorRightKidney = props.position.donor.whichKidney.toUpperCase().includes('RIGHT') || props.position.donor.whichKidney.toUpperCase().includes('R-OPT');

    const renderTableSection = (groups: any[], headerColor: string) => (
        <PlaceholderTable condensed>
            {groups.filter(x => x.options.length > 0).map(group => (
                <tbody key={'grp-' + group.id} style={{ borderTop: 'none' }}>
                    <tr
                        className="ph-sticky-header"
                        style={{
                            backgroundColor: headerColor,
                            color: 'white',
                            position: group.label == 'Target Centers' ? 'sticky' : 'static',
                            top: 0,
                            zIndex: 5
                        }}
                    >
                        <td className='ph-header-group' style={{ paddingLeft: 5, textAlign: 'left', width: '20%' }}
                            onClick={() => handlePlaceholderSort(group.id, 'alias', 'asc')}>
                            {group.label} {renderSortIndicator(group.id, 'alias')}
                        </td>
                        <td
                            className='ph-header-pc'
                            onClick={() => handlePlaceholderSort(group.id, 'isAdpPartner', 'desc')}
                            style={{ width: '8%' }}
                        >
                            <OverlayTrigger placement='bottom' trigger={['hover', 'focus']} overlay={<Tooltip id='pc-col-tooltip' className='ph-col-tooltip'>Partner Center</Tooltip>}>
                                <span>PC</span>
                            </OverlayTrigger> {renderSortIndicator(group.id, 'isAdpPartner')}
                        </td>

                        <td className='ph-header-fvt' onClick={() => handlePlaceholderSort(group.id, 'fvTarget', 'desc')} style={{ width: '8%' }}>
                            <OverlayTrigger placement='bottom' trigger={['hover', 'focus']} overlay={<Tooltip id='tce-col-tooltip' className='ph-col-tooltip'>Targeted Chain End</Tooltip>}>
                                <span>TCE</span>
                            </OverlayTrigger> {renderSortIndicator(group.id, 'fvTarget')}
                        </td>

                        <td className='ph-header-ncs' onClick={() => handlePlaceholderSort(group.id, 'ncsDeficit', 'desc')} style={{ width: '8%' }}>
                            <OverlayTrigger placement='bottom' trigger={['hover', 'focus']} overlay={<Tooltip id='ncs-col-tooltip' className='ph-col-tooltip'>Net Chains Started Score</Tooltip>}>
                                <span>NCSs</span>
                            </OverlayTrigger> {renderSortIndicator(group.id, 'ncsDeficit')}
                        </td>

                        <td className='ph-header-wdr' onClick={() => handlePlaceholderSort(group.id, 'declineRatio', 'asc')} style={{ width: '8%' }}>
                            <OverlayTrigger placement='bottom' trigger={['hover', 'focus']} overlay={<Tooltip id='wldr-col-tooltip' className='ph-col-tooltip'>Waitlist Decline Ratio</Tooltip>}>
                                <span>WL DR</span>
                            </OverlayTrigger> {renderSortIndicator(group.id, 'declineRatio')}
                        </td>

                        <td
                            className='ph-header-ap'
                            onClick={() => handlePlaceholderSort(group.id, 'anatomyPrefs', 'desc')}
                            style={{ width: '10%' }}
                        >
                            <OverlayTrigger placement='bottom' trigger={['hover', 'focus']} overlay={<Tooltip id='fnat-col-tooltip' className='ph-col-tooltip'>Functional Anatomy Preferences</Tooltip>}>
                                <span>FAnat</span>
                            </OverlayTrigger> {renderSortIndicator(group.id, 'anatomyPrefs')}
                        </td>

                        {(isAbAbo || isA2b) && (
                            <td className='ph-header-abwl' onClick={() => handlePlaceholderSort(group.id, 'hasAbPatients', 'desc')} style={{ width: '5%' }}>
                                <OverlayTrigger placement='bottom' trigger={['hover', 'focus']} overlay={<Tooltip id='ab-col-tooltip' className='ph-col-tooltip'>AB Waitlist Patients</Tooltip>}>
                                    <span>AB</span>
                                </OverlayTrigger> {renderSortIndicator(group.id, 'hasAbPatients')}
                            </td>
                        )}

                        {isA2OrA2b && (
                            <td
                                className='ph-header-a2-b'
                                onClick={() => handlePlaceholderSort(group.id, 'canUtilizeA2toBPatients', 'desc')}
                                style={{ width: '5%' }}
                            >
                                <OverlayTrigger placement='bottom' trigger={['hover', 'focus']} overlay={<Tooltip id='a2b-col-tooltip' className='ph-col-tooltip'>Can Utilize A2 to B Patients</Tooltip>}>
                                    <span>A2-B</span>
                                </OverlayTrigger> {renderSortIndicator(group.id, 'canUtilizeA2toBPatients')}
                            </td>
                        )}

                        <td
                            className='ph-header-dist'
                            onClick={() => handlePlaceholderSort(group.id, 'distance', 'asc')}
                            style={{ width: '10%' }}
                        >
                            Distance {renderSortIndicator(group.id, 'distance')}
                        </td>

                    </tr>

                    {group.options.map(option => {
                        const center = props.centerList.find(x => x.id == option.centerId);
                        let className = 'ph-row';
                        if (isA2OrA2b && center?.canUtilizeA2toBPatients) className = 'ph-row utilize-a2-b-patients';
                        if (option.declineRatio > 30) className = 'ph-row high-decline-ratio';

                        return (
                            <tr key={'opt-' + option.centerId} className={className} onClick={() => props.onCreatePlaceholder(option.centerId)}>
                                <td className='ph-cell' style={{ textAlign: 'left', paddingLeft: 5, width: '20%' }}>
                                    {option.alias}
                                </td>
                                <td className='ph-cell' style={{ width: '8%' }}>
                                    {option.isAdpPartner ? 'Y' : 'N'}
                                </td>
                                <td className='ph-cell' style={{ width: '8%' }}>{option.fvTarget}</td>
                                <td className='ph-cell' style={{ width: '8%' }}>{option.ncsDeficit}</td>
                                <td className='ph-cell' style={{ width: '8%' }}>{option.declineRatio}</td>
                                <td className='ph-cell' style={{ width: '10%', paddingRight: 10 }}>
                                    <OverlayTrigger trigger={['hover', 'hover']} overlay={
                                        <PopoverTemplate dismiss={false}>
                                            <TableWrapper bordered condensed>
                                                <thead>
                                                    <tr>
                                                        <th rowSpan={2}>Donor<br />Kidney</th>
                                                        <th colSpan={2}>{isDonorRightKidney ? 'Right' : 'Left'} Arteries</th>
                                                        <th colSpan={2}>{isDonorRightKidney ? 'Right' : 'Left'} Veins</th>
                                                    </tr>
                                                    <tr>
                                                        <th>Donor</th>
                                                        <th>Center</th>

                                                        <th>Donor</th>
                                                        <th>Center</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>{props.position.donor.whichKidney}</td>

                                                        <td>{props.position.donor.numberOfArteries}</td>
                                                        <td>
                                                            {isDonorRightKidney ?
                                                                center.anatomyPrefArteriesRight == AnatomyMaxPreference.Any ? 'Any' : center.anatomyPrefArteriesRight == AnatomyMaxPreference.WillNotAcceptRightKidney ? 'N/A' : center.anatomyPrefArteriesRight
                                                                :
                                                                center.anatomyPrefArteriesLeft == AnatomyMaxPreference.Any ? 'Any' : center.anatomyPrefArteriesLeft == AnatomyMaxPreference.WillNotAcceptRightKidney ? 'N/A' : center.anatomyPrefArteriesLeft
                                                            }
                                                        </td>

                                                        <td>{props.position.donor.numberOfVeins}</td>
                                                        <td>
                                                            {isDonorRightKidney ?
                                                                center.anatomyPrefVeinsRight == AnatomyMaxPreference.Any ? 'Any' : center.anatomyPrefVeinsRight == AnatomyMaxPreference.WillNotAcceptRightKidney ? 'N/A' : center.anatomyPrefVeinsRight
                                                                :
                                                                center.anatomyPrefVeinsLeft == AnatomyMaxPreference.Any ? 'Any' : center.anatomyPrefVeinsLeft == AnatomyMaxPreference.WillNotAcceptRightKidney ? 'N/A' : center.anatomyPrefVeinsLeft
                                                            }
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </TableWrapper>
                                        </PopoverTemplate>
                                    }>
                                        {option.anatomyPrefs ? <Glyphicon glyph='ok' style={{ lineHeight: 'initial', top: 2 }} /> : <Glyphicon glyph='remove' style={{ lineHeight: 'initial', top: 3, color: 'red' }} />}
                                    </OverlayTrigger>
                                </td>
                                {(isAbAbo || isA2b) && (
                                    <td className='ph-cell' style={{ width: '5%' }}>
                                        {option.hasAbPatients == null ? '--' : (option.hasAbPatients ? <Glyphicon glyph='ok' /> : <Glyphicon glyph='remove' style={{ color: 'red' }} />)}
                                    </td>
                                )}

                                {isA2OrA2b && (
                                    <td className='ph-cell' style={{ width: '5%' }}>
                                        {option.canUtilizeA2toBPatients ? <Glyphicon glyph='ok' /> : <Glyphicon glyph='remove' style={{ color: 'red' }} />}
                                    </td>
                                )}
                                <td className='ph-cell' style={{ width: '10%', paddingLeft: 10 }}>{group.label != 'Donor Center' ? option.distance : '--'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            ))}
        </PlaceholderTable>
    );

    return (
        <>
            <TooltipGlobalStyle />
            {/* Legend */}
            <div style={{ padding: 5, width: '100%', backgroundColor: '#eee' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <strong style={{ marginRight: 10 }}>Legend:</strong>
                    <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                        <div style={{ height: 20, width: 20, backgroundColor: 'white', border: '1px solid black', marginRight: 5 }}></div>
                        {'Waitlist Decline Ratio < 30%'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 10, marginRight: 10 }}>
                        <div style={{ height: 20, width: 20, backgroundColor: 'lightgrey', border: '1px solid black', marginRight: 5 }}></div>
                        {'Waitlist Decline Ratio >= 30%'}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                        <ToggleButtonGroup type='radio' name='suppress' value={suppress ? 1 : 0}>
                            <OverlayTrigger placement='bottom' overlay={
                                <ToggleTooltip id='suppress-tooltip' title='Hide the following:'
                                    items={['Non-Partner centers, NCSs < 2', 'WLDR ≥ 30%', 'FAnat = "X"', ...(isAbAbo ? ['AB = "X"'] : []), ...(isA2b ? ['AB = "X"', 'A2B = "X"'] : []), ...(isA2 ? ['A2B = "X"'] : [])]} />
                            }>
                                <ToggleButton type='radio' value={1} className={suppress ? 'btn-primary btn-sm' : 'btn-sm'} onClick={() => handleUpdateSuppress(true)}>Suppress</ToggleButton>
                            </OverlayTrigger>

                            <OverlayTrigger placement='bottom' overlay={
                                <ToggleTooltip id='unsuppress-tooltip' title='Show the following:'
                                    items={['Non-Partner centers, NCSs > 0', 'Partner centers, any TCE', 'Targets w/o AB visibility', 'Targets w/o A2/A2B visibility']} />
                            }>
                                <ToggleButton type='radio' value={0} className={!suppress ? 'btn-primary btn-sm' : 'btn-sm'} onClick={() => handleUpdateSuppress(false)}>Unsuppress</ToggleButton>
                            </OverlayTrigger>
                        </ToggleButtonGroup>
                    </div>
                </div>
            </div>
            <div style={{ padding: '0 5px', height: 400, overflowY: 'scroll', width: '100%', borderBottom: '1px solid #ddd' }}>
                {/* Within 400 Miles */}
                <div style={{ display: 'flex', alignItems: 'center', margin: '10px 0' }}>
                    <div style={{ flex: 1, borderTop: '1px solid #e0e0e0' }}></div>
                    <span style={{ padding: '0 10px', color: 'black', fontWeight: 500, fontSize: 14 }}>Within 400 miles</span>
                    <div style={{ flex: 1, borderTop: '1px solid #e0e0e0' }}></div>
                </div>

                {renderTableSection(donorCenterGroup, '#123666')}
                {renderTableSection(targetWithin400, '#2a8000')}

                {/* Beyond 400 Miles */}
                <div style={{ display: 'flex', alignItems: 'center', margin: '10px 0' }}>
                    <div style={{ flex: 1, borderTop: '1px solid #e0e0e0' }}></div>
                    <span style={{ padding: '0 10px', color: 'black', fontWeight: 500, fontSize: 14 }}>Greater than 400 miles away</span>
                    <div style={{ flex: 1, borderTop: '1px solid #e0e0e0' }}></div>
                </div>

                {renderTableSection(targetBeyond400, '#2a8000')}
            </div>
        </>
    );
};

export default PlaceHolderTable;
